'use client';

import { Services } from '@/infra/services';
import { type ModularContent } from '@/infra/services/modular-content/model';
import {
  ProductsCarousel,
  ProductsCarouselSkeleton,
  type ProductsCarouselProps,
} from '@/presenters/components/common/ProductsCarousel';
import { useRecommendedProducts } from '@/presenters/hooks/use-recommended-products';
import { useUserResources } from '@/presenters/hooks/user';
import { useModularContentStore } from '@/presenters/store/modular-content';

export interface RecommendedProductsCarouselProps {
  type: ModularContent.RecommendedProducts['type'];
  placement: string;
}

function RecommendedProductsCarousel(props: RecommendedProductsCarouselProps) {
  const { placement, type } = props;

  const { data: userId } = useUserResources((data) => data.id);

  const {
    data: placementsData,
    isError: isPlacementsError,
    isLoading: isLoadingPlacements,
  } = useModularContentStore((state) => state.recommendations);

  const recommendation = placementsData?.[placement];

  const {
    data: productsData,
    isError: isProductsError,
    isLoading: isLoadingProducts,
  } = useRecommendedProducts(recommendation, userId);

  const handleProductClick: ProductsCarouselProps['onProductClick'] = async (
    event,
    product
  ) => {
    event.preventDefault();

    product.trackingUrl &&
      (await Services.RichRelevance.tracking({ url: product.trackingUrl }));

    window.location.href = product.url;
  };

  const hasError = isPlacementsError || isProductsError;
  const hasNoProducts = !isLoadingProducts && !productsData?.products.length;

  if (hasError || hasNoProducts) {
    return <></>;
  }

  if (isLoadingPlacements || isLoadingProducts) {
    return <ProductsCarouselSkeleton testId="recommended-products-loading" />;
  }

  return (
    <ProductsCarousel
      partnerName="algonomy"
      type={type}
      title={productsData?.title || ''}
      items={productsData?.products || []}
      onProductClick={handleProductClick}
    />
  );
}

export default RecommendedProductsCarousel;
